import React from 'react';

import { Button } from '@mui/material';
import { LinkProps } from 'react-router-dom';
import styled from 'styled-components';

import { SYNMATCH_COLORS, SYNMATCH_RADII } from 'theme';

export const PUBLIC_HEADER_OFFSET = '6rem';

export const PublicPageShell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${SYNMATCH_COLORS.surface.default};
`;

export const PublicMain = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const PublicContainer = styled.div`
  width: 100%;
  max-width: 78rem;
  margin-inline: auto;
  padding-inline: clamp(1.25rem, 5vw, 6.5rem);
`;

export const PublicSiteHeaderBar = styled.header`
  position: sticky;
  top: 0;
  z-index: ${props => props.theme.zIndex.appBar};
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 4rem;
  background: ${SYNMATCH_COLORS.surface.headerScrim};
  -webkit-backdrop-filter: saturate(140%) blur(10px);
  backdrop-filter: saturate(140%) blur(10px);
  border-bottom: 0.0625rem solid ${SYNMATCH_COLORS.border.default};
  padding-block: 1rem;

  @media (max-width: 30rem) {
    padding-block: 0.875rem;
  }
`;

export const PublicSiteHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  @media (max-width: 30rem) {
    gap: 1rem;
  }
`;

export const PublicSiteHeaderHomeLink = styled.a`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  border-radius: ${SYNMATCH_RADII.sm};

  &:focus-visible {
    outline: 0.125rem solid ${SYNMATCH_COLORS.primary};
    outline-offset: 0.25rem;
  }
`;

export const PublicSiteHeaderWordmark = styled.span`
  display: inline-flex;
  align-items: center;

  @media (max-width: 30rem) {
    display: none;
  }
`;

export const PublicSiteHeaderEmblem = styled.span`
  display: none;
  align-items: center;

  @media (max-width: 30rem) {
    display: inline-flex;
  }
`;

export const PublicSiteHeaderActions = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  white-space: nowrap;

  @media (max-width: 30rem) {
    gap: 0.375rem;

    .MuiButton-root {
      padding-block: 0.375rem;
      padding-inline: 0.625rem;
      font-size: 0.8125rem;
    }
  }
`;

export const PublicSiteSecondaryCta = styled(Button).attrs({
  disableRipple: true,
  variant: 'outlined',
})<{ component?: React.ElementType; to?: LinkProps['to'] }>`
  && {
    border-color: ${SYNMATCH_COLORS.border.default};
    color: ${SYNMATCH_COLORS.text.default};

    &:hover {
      border-color: ${SYNMATCH_COLORS.text.muted};
      background-color: ${SYNMATCH_COLORS.surface.app};
    }
  }
`;

export const PublicFooterBar = styled.footer`
  border-top: 0.0625rem solid ${SYNMATCH_COLORS.border.default};
  padding-block: 2rem;
`;

export const PublicFooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 2rem;
  flex-wrap: wrap;
`;

export const PublicFooterBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const PublicFooterTagline = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${SYNMATCH_COLORS.text.muted};
`;

export const PublicFooterMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.8125rem;
  color: ${SYNMATCH_COLORS.text.muted};
`;

export const PublicFooterLink = styled.a`
  color: ${SYNMATCH_COLORS.text.muted};
  text-decoration: none;
  border-radius: ${SYNMATCH_RADII.sm};

  &:hover {
    color: ${SYNMATCH_COLORS.primary};
  }

  &:focus-visible {
    outline: 0.125rem solid ${SYNMATCH_COLORS.primary};
    outline-offset: 0.25rem;
  }
`;

export const PublicFooterSeparator = styled.span`
  color: ${SYNMATCH_COLORS.border.default};
`;
